import React from "react"
import { Card } from "antd"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faMagnifyingGlass,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons"

import { DOCS_SECTIONS, pageRoute } from "features/Docs/manifest"

interface DocsLandingProps {
  onSearch: () => void
}

/** Short, human descriptions for each section shown on the landing page. */
const SECTION_BLURBS: Record<string, string> = {
  "getting-started": "New here? Start with what DATA(Q) is and how the pieces fit together.",
  "format-guide": "Write questionnaires: questions, lists, routing, loops, and everything in the survey format.",
  "query-language": "Turn responses into crosstabs, tables, and exports with the query language.",
  "best-practices": "Patterns worth copying and the traps worth knowing before they cost you.",
}

/**
 * The docs home: a brief hero with a prominent search action, then a card per
 * section linking into its first page.
 */
const DocsLanding: React.FC<DocsLandingProps> = ({ onSearch }) => (
  <div className="docs-landing">
    <header className="docs-landing__hero">
      <h1 className="docs-landing__title">
        <img
          className="docs-landing__logo"
          src="/assets/images/data-q-logo-black-n-blue.png"
          alt="DATA(Q)"
        />
        Documentation
      </h1>
      <p className="docs-landing__lede">
        Everything you need to author questionnaires and query your data —
        from your first question to weighted exports.
      </p>
      <button
        type="button"
        className="docs-landing__search"
        onClick={onSearch}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <span>Search the docs</span>
        <span className="docs-landing__kbd">
          <kbd>Ctrl</kbd>
          <kbd>K</kbd>
        </span>
      </button>
    </header>

    <div className="docs-landing__grid">
      {DOCS_SECTIONS.map((section) => {
        const firstPage = section.pages[0]
        return (
          <Card
            key={section.slug}
            className="docs-landing__card"
            variant="outlined"
          >
            <Link
              to={pageRoute(section.slug, firstPage.slug)}
              className="docs-landing__card-link"
            >
              <h2 className="docs-landing__card-title">{section.title}</h2>
              <p className="docs-landing__card-desc">
                {SECTION_BLURBS[section.slug]}
              </p>
              <div className="docs-landing__card-footer">
                <span className="docs-landing__card-count">
                  {section.pages.length}{" "}
                  {section.pages.length === 1 ? "page" : "pages"}
                </span>
                <span className="docs-landing__card-cta">
                  Start reading <FontAwesomeIcon icon={faArrowRight} />
                </span>
              </div>
            </Link>
          </Card>
        )
      })}
    </div>
  </div>
)

export default DocsLanding
